import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Github,
  Linkedin,
  ArrowRight,
  Twitter,
  BookOpen,
  FileText,
  Mail,
  Code,
  Server,
  Cpu,
  Boxes,
  CodepenIcon,
  Brain,
  Zap,
  Award,
} from "lucide-react";

import ProjectCard from "../../components/ui/ProjectCard";
import ProjectDetailModal from "../../components/ui/ProjectDetailModal";
import SectionHeader from "../../components/ui/SectionHeader";
import StatCard from "../../components/ui/StatCard";
import ExperienceTimeline from "../../components/ui/ExperienceTimeline";
import ContactForm from "../../components/forms/ContactForm";
import { normalizeProjectLinks } from "../../utils/projectLinks";
import { useProfile } from "../../contexts/ProfileContext";
import { usePublications } from "../../contexts/PublicationContext";
import { usePubPackages } from "../../contexts/PubPackageContext";
import { useStarterTemplates } from "../../contexts/StarterTemplateContext";
import { useBlogs } from "../../contexts/BlogContext";
import { useExperiences } from "../../contexts/ExperienceContext";
import { getFeaturedProjects, Project } from "../../firebase/projectService";
import { usePageTitle } from "../../hooks/usePageTitle";
import { deriveStats, deriveCurrentRole } from "../../utils/profileInsights";
import {
  EnvelopeIcon,
  PhoneIcon,
  MapPinIcon,
  BeakerIcon,
  CodeBracketIcon,
  WrenchScrewdriverIcon,
  DocumentTextIcon,
  HeartIcon,
  ArrowDownTrayIcon,
  PlayIcon,
  ClockIcon,
  StarIcon as StarOutlineIcon,
} from "@heroicons/react/24/outline";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

const HomePage: React.FC = () => {
  usePageTitle("Home");

  const { profile, loading } = useProfile();
  const { publications } = usePublications();
  const { featuredPubPackages } = usePubPackages();
  const { featuredStarterTemplates } = useStarterTemplates();
  const { featuredBlogs } = useBlogs();
  const { experiences } = useExperiences();

  const [featuredProjects, setFeaturedProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);
  const [selectedProject, setSelectedProject] = useState<Project | null>(null);

  useEffect(() => {
    setProjectsLoading(true);
    setProjectsError(null);
    getFeaturedProjects()
      .then((projects) => {
        setFeaturedProjects(projects);
        setProjectsLoading(false);
      })
      .catch(() => {
        setProjectsError("Failed to load featured projects");
        setProjectsLoading(false);
      });
  }, []);

  if (loading) {
    return (
      <div className="flex h-[80vh] items-center justify-center">
        <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600" />
      </div>
    );
  }

  const stats = deriveStats(profile, experiences);
  const currentRole = deriveCurrentRole(experiences);

  const projectsDescription =
    profile.projectsDescription ||
    "Selected work across mobile, web, and AI/ML — focused on scope, role, and measurable impact.";

  return (
    <div>
      {/* ============================ HERO ============================ */}
      <section className="relative overflow-hidden bg-gradient-to-br from-primary-50 via-white to-secondary-50 dark:from-gray-900 dark:via-gray-800 dark:to-primary-900/30">
        <div className="container py-20 md:py-28">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
            {/* Content */}
            <motion.div
              className="lg:col-span-7"
              initial={{ opacity: 0, x: -32 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <span className="eyebrow">Available for opportunities</span>

              <h1 className="mt-5 text-display text-gray-900 dark:text-white">
                {profile.fullName}
              </h1>

              <p className="mt-4 text-xl md:text-2xl font-medium text-primary-600 dark:text-primary-400">
                {profile.title}
              </p>

              <p className="mt-5 max-w-xl text-base md:text-lg leading-relaxed text-gray-600 dark:text-gray-400">
                {profile.shortBio || profile.bio}
              </p>

              {/* Credentials row */}
              <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm">
                {stats[0] && (
                  <span className="font-semibold text-gray-900 dark:text-white">
                    {stats[0].value}{" "}
                    <span className="font-normal text-gray-500 dark:text-gray-400">years experience</span>
                  </span>
                )}
                {currentRole && (
                  <span className="font-semibold text-gray-900 dark:text-white">
                    Now:{" "}
                    <span className="font-normal text-gray-500 dark:text-gray-400">
                      {currentRole.position} @ {currentRole.company}
                    </span>
                  </span>
                )}
              </div>

              {/* CTAs */}
              <div className="mt-8 flex flex-wrap items-center gap-3">
                <Link to="/resume" className="btn-primary inline-flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  View Résumé
                </Link>
                <Link to="/contact" className="btn-outline inline-flex items-center gap-2">
                  <Mail className="h-4 w-4" />
                  Get in Touch
                </Link>

                <div className="flex items-center gap-2">
                  {profile.socialLinks?.linkedin && (
                    <a
                      href={profile.socialLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn profile"
                      className="rounded-full bg-blue-100 p-2.5 text-blue-600 transition-colors hover:bg-blue-200 dark:bg-blue-900/30 dark:text-blue-400 dark:hover:bg-blue-800/50"
                    >
                      <Linkedin className="h-5 w-5" />
                    </a>
                  )}
                  {profile.socialLinks?.github && (
                    <a
                      href={profile.socialLinks.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="GitHub profile"
                      className="rounded-full bg-gray-100 p-2.5 text-gray-700 transition-colors hover:bg-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                    >
                      <Github className="h-5 w-5" />
                    </a>
                  )}
                  {profile.socialLinks?.twitter && (
                    <a
                      href={profile.socialLinks.twitter}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Twitter profile"
                      className="rounded-full bg-sky-100 p-2.5 text-sky-500 transition-colors hover:bg-sky-200 dark:bg-sky-900/30 dark:text-sky-400 dark:hover:bg-sky-800/50"
                    >
                      <Twitter className="h-5 w-5" />
                    </a>
                  )}
                  {profile.socialLinks?.googleScholar && (
                    <a
                      href={profile.socialLinks.googleScholar}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="Google Scholar profile"
                      className="rounded-full bg-orange-100 p-2.5 text-orange-600 transition-colors hover:bg-orange-200 dark:bg-orange-900/30 dark:text-orange-400 dark:hover:bg-orange-800/50"
                    >
                      <BookOpen className="h-5 w-5" />
                    </a>
                  )}
                </div>
              </div>

              {/* Core expertise */}
              <div className="mt-8">
                <h3 className="mb-3 text-sm font-medium text-gray-500 dark:text-gray-400">
                  Core expertise:
                </h3>
                <div className="grid max-w-2xl grid-cols-2 gap-2 sm:grid-cols-3 md:grid-cols-4">
                  <div className="flex items-center rounded-lg border border-primary-200 bg-gradient-to-r from-primary-50 to-primary-100 px-3 py-2 dark:border-primary-800 dark:from-primary-900/20 dark:to-primary-900/10">
                    <Code className="mr-2 h-4 w-4 text-primary-500" />
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Team Leadership</span>
                  </div>
                  <div className="flex items-center rounded-lg border border-blue-200 bg-gradient-to-r from-blue-50 to-blue-100 px-3 py-2 dark:border-blue-800 dark:from-blue-900/20 dark:to-blue-900/10">
                    <Server className="mr-2 h-4 w-4 text-blue-500" />
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Project Management</span>
                  </div>
                  <div className="flex items-center rounded-lg border border-violet-200 bg-gradient-to-r from-violet-50 to-violet-100 px-3 py-2 dark:border-violet-800 dark:from-violet-900/20 dark:to-violet-900/10">
                    <Cpu className="mr-2 h-4 w-4 text-violet-500" />
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Agile / Scrum</span>
                  </div>
                  <div className="flex items-center rounded-lg border border-amber-200 bg-gradient-to-r from-amber-50 to-amber-100 px-3 py-2 dark:border-amber-800 dark:from-amber-900/20 dark:to-amber-900/10">
                    <Boxes className="mr-2 h-4 w-4 text-amber-500" />
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Architecture</span>
                  </div>
                  <div className="flex items-center rounded-lg border border-cyan-200 bg-gradient-to-r from-cyan-50 to-cyan-100 px-3 py-2 dark:border-cyan-800 dark:from-cyan-900/20 dark:to-cyan-900/10">
                    <CodepenIcon className="mr-2 h-4 w-4 text-cyan-500" />
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">CI/CD &amp; DevOps</span>
                  </div>
                  <div className="flex items-center rounded-lg border border-indigo-200 bg-gradient-to-r from-indigo-50 to-indigo-100 px-3 py-2 dark:border-indigo-800 dark:from-indigo-900/20 dark:to-indigo-900/10">
                    <CodepenIcon className="mr-2 h-4 w-4 text-indigo-500" />
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Native iOS &amp; Android</span>
                  </div>
                  <div className="flex items-center rounded-lg border border-pink-200 bg-gradient-to-r from-pink-50 to-pink-100 px-3 py-2 dark:border-pink-800 dark:from-pink-900/20 dark:to-pink-900/10">
                    <Brain className="mr-2 h-4 w-4 text-pink-500" />
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">Flutter</span>
                  </div>
                  <div className="flex items-center rounded-lg border border-green-200 bg-gradient-to-r from-green-50 to-green-100 px-3 py-2 dark:border-green-800 dark:from-green-900/20 dark:to-green-900/10">
                    <Zap className="mr-2 h-4 w-4 text-green-500" />
                    <span className="text-sm font-medium text-gray-800 dark:text-gray-200">AI / ML</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Photo */}
            <motion.div
              className="flex justify-center lg:col-span-5"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute inset-0 translate-x-4 translate-y-4 rounded-full bg-primary-200 dark:bg-primary-900/40" />
                <div className="relative z-10 h-64 w-64 overflow-hidden rounded-full border-4 border-white shadow-xl dark:border-gray-800 md:h-80 md:w-80">
                  <img
                    src={profile.photoURL}
                    alt={profile.fullName}
                    className="h-full w-full object-cover"
                  />
                </div>

                {/* PMP Certification Badge */}
                {profile.pmpBadgeUrl ? (
                  <div className="absolute -top-2 -left-2 z-20 rounded-xl bg-white dark:bg-gray-800 p-1 shadow-lg border-2 border-white dark:border-gray-700">
                    <img
                      src={profile.pmpBadgeUrl}
                      alt="PMP Certified"
                      className="h-16 w-16 md:h-20 md:w-20 rounded-lg object-contain"
                    />
                  </div>
                ) : (
                  <div className="absolute -top-2 -left-2 z-20 rounded-xl bg-gradient-to-br from-amber-500 to-amber-600 px-4 py-2.5 shadow-lg border-2 border-white dark:border-gray-800">
                    <div className="flex items-center gap-2">
                      <Award className="h-5 w-5 text-white" />
                      <div className="text-left">
                        <span className="block text-xs font-bold text-white leading-tight">PMP</span>
                        <span className="block text-xs text-amber-100 leading-tight">Certified</span>
                      </div>
                    </div>
                  </div>
                )}

                {currentRole && (
                  <div className="absolute -bottom-4 -right-2 z-20 rounded-xl bg-white px-4 py-2.5 shadow-lg dark:bg-gray-800">
                    <span className="block text-xs text-gray-500 dark:text-gray-400">Currently at</span>
                    <span className="text-sm font-bold text-primary-600 dark:text-primary-400">
                      {currentRole.company}
                    </span>
                  </div>
                )}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ============================ IMPACT STATS ============================ */}
      {stats.length > 0 && (
        <section className="border-y border-gray-200 bg-white dark:border-gray-800 dark:bg-gray-900">
          <div className="container">
            <dl className="grid grid-cols-2 divide-x divide-gray-200 dark:divide-gray-800 md:grid-cols-4">
              {stats.map((stat) => (
                <div key={stat.label}>
                  <StatCard value={stat.value} label={stat.label} />
                </div>
              ))}
            </dl>
          </div>
        </section>
      )}

      {/* ============================ ABOUT ============================ */}
      <section className="section bg-gray-50 dark:bg-gray-800">
        <div className="container">
          <SectionHeader
            eyebrow="About"
            title="Engineering leadership, built over a career"
            actionLabel="More about me"
            actionTo="/about"
          />
          <div className="grid gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <p className="text-lg leading-relaxed text-gray-700 dark:text-gray-300">
                {profile.bio}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              {(profile.softSkills || []).slice(0, 4).map((skill: any) => (
                <div
                  key={skill.id}
                  className="rounded-xl border-l-4 border-primary-500 bg-white p-4 shadow-sm dark:bg-gray-900/40"
                >
                  <h3 className="font-semibold text-gray-900 dark:text-white">{skill.title}</h3>
                  {skill.description && (
                    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400 line-clamp-2">
                      {skill.description}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ============================ EXPERIENCE ============================ */}
      {experiences && experiences.length > 0 && (
        <section className="section bg-white dark:bg-gray-900">
          <div className="container">
            <SectionHeader
              eyebrow="Experience"
              title="Career timeline"
              description="A progression from hands-on mobile engineer to development manager leading cross-functional teams."
              actionLabel="Full resume"
              actionTo="/resume"
            />
            <div className="max-w-3xl">
              <ExperienceTimeline experiences={experiences} limit={4} />
            </div>
          </div>
        </section>
      )}

      {/* ============================ SKILLS ============================ */}
      <section className="section bg-gray-50 dark:bg-gray-800">
        <div className="container">
          <SectionHeader
            eyebrow="Expertise"
            title="Technical skills"
            description={profile.skillsDescription}
            actionLabel="View full resume"
            actionTo="/resume"
          />

          <div className="grid gap-6 md:grid-cols-2">
            {(profile.skills || []).map((category: any) => (
              <motion.div
                key={category.id}
                variants={fadeUp}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-60px" }}
                className="card p-6"
              >
                <h3 className="mb-4 flex items-center gap-3 text-lg font-bold text-gray-900 dark:text-white">
                  <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-primary-600 text-sm font-bold text-white">
                    {category.category.charAt(0)}
                  </span>
                  {category.category}
                </h3>

                {category.subcategories ? (
                  <div className="space-y-4">
                    {category.subcategories.map((sub: any, si: number) => (
                      <div key={si}>
                        <h4 className="mb-2 text-sm font-semibold text-gray-500 dark:text-gray-400">
                          {sub.name}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {sub.skills.map((skill: string, ski: number) => (
                            <span key={ski} className="chip-neutral">
                              {skill}
                            </span>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="flex flex-wrap gap-2">
                    {category.items?.map((skill: string, i: number) => (
                      <span key={i} className="chip-neutral">
                        {skill}
                      </span>
                    ))}
                  </div>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* ============================ FEATURED PROJECTS ============================ */}
      <section className="section bg-white dark:bg-gray-900">
        <div className="container">
          <SectionHeader
            eyebrow="Projects"
            title="Featured projects"
            description={projectsDescription}
            actionLabel="View all projects"
            actionTo="/projects"
          />

          {projectsLoading ? (
            <div className="flex justify-center py-12">
              <div className="h-12 w-12 animate-spin rounded-full border-b-2 border-primary-600" />
            </div>
          ) : projectsError ? (
            <p className="py-8 text-center text-red-500">{projectsError}</p>
          ) : featuredProjects.length === 0 ? (
            <p className="py-8 text-center text-gray-500 dark:text-gray-400">
              No projects available yet.
            </p>
          ) : (
            <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
              {featuredProjects.map((project) => (
                <button
                  key={project.id}
                  onClick={() => setSelectedProject(project)}
                  className="h-full cursor-pointer text-left"
                  aria-label={`View details for ${project.title}`}
                >
                  <ProjectCard
                    title={project.title}
                    description={project.description}
                    imageUrl={project.image}
                    tags={project.tags}
                    role={project.myRole}
                    company={project.company}
                    projectType={project.projectType}
                    platforms={normalizeProjectLinks(project).map((l) => l.type)}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ============================ ACHIEVEMENTS ============================ */}
      {profile.certifications && profile.certifications.length > 0 && (
        <section className="section bg-gray-50 dark:bg-gray-800">
          <div className="container">
            <SectionHeader
              eyebrow="Credentials"
              title="Certifications"
              actionLabel="Full profile"
              actionTo="/about"
            />
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {profile.certifications.slice(0, 6).map((cert) => (
                <motion.div
                  key={cert.id}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-60px" }}
                  className="card flex flex-col overflow-hidden"
                >
                  {cert.imageUrl && (
                    <div className="w-full h-32 bg-gray-100 dark:bg-gray-700 flex items-center justify-center overflow-hidden">
                      <img
                        src={cert.imageUrl}
                        alt={cert.name}
                        className="w-full h-full object-contain p-2"
                      />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <h3 className="font-semibold text-gray-900 dark:text-white">{cert.name}</h3>
                    <p className="mt-1 text-sm text-gray-600 dark:text-gray-400">{cert.issuer}</p>
                    <p className="text-sm text-gray-400 dark:text-gray-500">{cert.date}</p>
                    {cert.url && (
                      <a
                        href={cert.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-auto inline-flex items-center gap-1 pt-4 text-sm font-semibold text-primary-600 dark:text-primary-400 hover:underline"
                      >
                        View certificate
                        <ArrowRight className="h-3.5 w-3.5" />
                      </a>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============================ OPEN SOURCE ============================ */}
      <section className="section bg-white dark:bg-gray-900">
        <div className="container">
          <SectionHeader
            eyebrow="Open Source"
            title="Community contributions"
            description="Packages, tools, and tutorials that help other developers ship faster."
            align="center"
          />

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            {/* Pub packages */}
            <div className="card p-7">
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-xl bg-gradient-to-br from-blue-500 to-purple-600 p-3">
                  <CodeBracketIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Flutter Packages</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Published on pub.dev</p>
                </div>
              </div>
              {featuredPubPackages.slice(0, 2).map((pkg) => (
                <div key={pkg.id} className="mb-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-900/40">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900 dark:text-white">{pkg.name}</h4>
                    <span className="text-xs text-blue-600 dark:text-blue-400">v{pkg.version}</span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                    {pkg.description}
                  </p>
                  <div className="mt-2 flex gap-3 text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <ArrowDownTrayIcon className="h-3 w-3" />
                      {pkg.downloads ? (pkg.downloads >= 1000 ? `${(pkg.downloads / 1000).toFixed(1)}K` : pkg.downloads) : 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <HeartIcon className="h-3 w-3" />
                      {pkg.likes || 0}
                    </span>
                  </div>
                </div>
              ))}
              <Link to="/pub-packages" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 dark:text-primary-400">
                View all packages
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Dev tools */}
            <div className="card p-7">
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 p-3">
                  <WrenchScrewdriverIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Dev Tools &amp; Templates</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Starter kits &amp; tools</p>
                </div>
              </div>
              {featuredStarterTemplates.slice(0, 2).map((template) => (
                <div key={template.id} className="mb-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-900/40">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900 dark:text-white">{template.title}</h4>
                    <span className="rounded-full bg-purple-100 px-2 py-0.5 text-xs text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                      {template.type}
                    </span>
                  </div>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">
                    {template.description}
                  </p>
                  <div className="mt-2 flex gap-3 text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <ArrowDownTrayIcon className="h-3 w-3" />
                      {template.downloads ? (template.downloads >= 1000 ? `${(template.downloads / 1000).toFixed(1)}K` : template.downloads) : 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <StarOutlineIcon className="h-3 w-3" />
                      {template.stars || 0}
                    </span>
                  </div>
                </div>
              ))}
              <Link to="/starter-templates" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 dark:text-primary-400">
                Explore tools
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>

            {/* Tutorials */}
            <div className="card p-7">
              <div className="mb-5 flex items-center gap-3">
                <div className="rounded-xl bg-gradient-to-br from-green-500 to-blue-600 p-3">
                  <DocumentTextIcon className="h-6 w-6 text-white" />
                </div>
                <div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Tutorials &amp; Articles</h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">Bengali &amp; English</p>
                </div>
              </div>
              {featuredBlogs.slice(0, 2).map((blog) => (
                <div key={blog.id} className="mb-3 rounded-lg bg-gray-50 p-3 dark:bg-gray-900/40">
                  <div className="flex items-center justify-between">
                    <h4 className="font-semibold text-gray-900 dark:text-white line-clamp-1">{blog.title}</h4>
                    {blog.youtubeVideoId && <PlayIcon className="h-4 w-4 shrink-0 text-red-600" />}
                  </div>
                  <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 line-clamp-2">{blog.excerpt}</p>
                  <div className="mt-2 flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                    <span className="flex items-center gap-1">
                      <ClockIcon className="h-3 w-3" />
                      {blog.readingTime} min
                    </span>
                    <span className="text-indigo-600 dark:text-indigo-400">{blog.language}</span>
                  </div>
                </div>
              ))}
              <Link to="/blogs" className="mt-3 inline-flex items-center gap-1.5 text-sm font-semibold text-primary-600 dark:text-primary-400">
                Read all tutorials
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ============================ RESEARCH ============================ */}
      {profile.researchInterests && profile.researchInterests.length > 0 && (
        <section className="section bg-gray-50 dark:bg-gray-800">
          <div className="container">
            <SectionHeader
              eyebrow="Research"
              title="Research focus"
              description={profile.researchSummary}
              actionLabel={publications && publications.length > 0 ? "View publications" : undefined}
              actionTo={publications && publications.length > 0 ? "/publications" : undefined}
            />
            <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
              {profile.researchInterests.slice(0, 3).map((interest) => (
                <motion.div
                  key={interest.id}
                  variants={fadeUp}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true, margin: "-60px" }}
                  className="card p-7"
                >
                  <div className="mb-4 inline-flex rounded-full bg-primary-100 p-2.5 dark:bg-primary-900/30">
                    <BeakerIcon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{interest.title}</h3>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">{interest.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ============================ CONTACT ============================ */}
      <section className="section bg-white dark:bg-gray-900">
        <div className="container">
          <div className="overflow-hidden rounded-2xl shadow-xl">
            <div className="flex flex-col lg:flex-row">
              {/* Info */}
              <div className="bg-gradient-to-br from-primary-600 to-primary-800 p-10 text-white lg:w-2/5">
                <h2 className="text-3xl font-bold">Let's work together</h2>
                <p className="mt-4 text-primary-100">
                  Open to engineering leadership roles, consulting, and collaboration. The fastest way to
                  reach me is below.
                </p>

                <div className="mt-8 space-y-5">
                  <a href={`mailto:${profile.email}`} className="flex items-center gap-4 hover:opacity-90">
                    <span className="rounded-full bg-white/15 p-3">
                      <EnvelopeIcon className="h-5 w-5" />
                    </span>
                    <span>
                      <span className="block text-sm text-primary-200">Email</span>
                      <span className="font-medium">{profile.email}</span>
                    </span>
                  </a>
                  <div className="flex items-center gap-4">
                    <span className="rounded-full bg-white/15 p-3">
                      <PhoneIcon className="h-5 w-5" />
                    </span>
                    <span>
                      <span className="block text-sm text-primary-200">Phone</span>
                      <span className="font-medium">{profile.phone || "Contact via email"}</span>
                    </span>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="rounded-full bg-white/15 p-3">
                      <MapPinIcon className="h-5 w-5" />
                    </span>
                    <span>
                      <span className="block text-sm text-primary-200">Location</span>
                      <span className="font-medium">{profile.location || "Remote"}</span>
                    </span>
                  </div>
                </div>

                <div className="mt-10 flex flex-wrap gap-3">
                  <Link
                    to="/resume"
                    className="inline-flex items-center gap-2 rounded-xl bg-white px-5 py-2.5 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-50"
                  >
                    <ArrowDownTrayIcon className="h-4 w-4" />
                    Résumé
                  </Link>
                  {profile.socialLinks?.linkedin && (
                    <a
                      href={profile.socialLinks.linkedin}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="LinkedIn profile"
                      className="rounded-full bg-white/15 p-3 transition-colors hover:bg-white/25"
                    >
                      <Linkedin className="h-5 w-5" />
                    </a>
                  )}
                  {profile.socialLinks?.github && (
                    <a
                      href={profile.socialLinks.github}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label="GitHub profile"
                      className="rounded-full bg-white/15 p-3 transition-colors hover:bg-white/25"
                    >
                      <Github className="h-5 w-5" />
                    </a>
                  )}
                </div>
              </div>

              {/* Form */}
              <div className="bg-white p-10 dark:bg-gray-800 lg:w-3/5">
                <h3 className="mb-6 text-2xl font-semibold text-gray-900 dark:text-white">
                  Send me a message
                </h3>
                <ContactForm />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Project detail modal */}
      <ProjectDetailModal project={selectedProject} onClose={() => setSelectedProject(null)} />
    </div>
  );
};

export default HomePage;
